import React, { useState, useEffect, useMemo } from "react";
import { fmtDateTime } from "../lib/dates";
import { toast } from "sonner";
import { MailOpen, Clock, MailWarning, RefreshCw, GitMerge, AlertTriangle, ChevronDown } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { InviteSweep } from "./InviteSweep";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const STATE = {
  opened: [MailOpen, "bg-palm/10 text-palm", "Link opened"],
  sent: [Clock, "bg-gold-light text-crimson", "Sent · not opened"],
  expired: [MailWarning, "bg-red-100 text-red-800", "Link expired"],
  none: [MailWarning, "bg-parchment text-muted-foreground", "No invite yet"],
};

function InviteRow({ m, busy, onRefresh }) {
  const [Icon, cls, label] = STATE[m.invite];
  const when = (ts, verb) => (ts ? ` · ${verb} ${fmtDateTime(ts)}` : "");
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gold/30 bg-parchment-card p-3" data-testid={`invite-row-${m.id}`}>
      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
        <p className="truncate font-semibold">{m.name} <span className="font-mono-accent text-xs text-gold">{m.member_id}</span>{m.status === "pending" && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">pending</span>}</p>
        <p className="truncate text-xs text-muted-foreground">{m.email}{when(m.expires_at, "expires")}{when(m.opened_at, "opened")}</p>
      </div>
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`invite-state-${m.id}`}><Icon size={13} /> {label}</span>
      <Button size="sm" variant="outline" disabled={busy} onClick={onRefresh} className="gap-1" data-testid={`invite-refresh-${m.id}`}><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {m.invite === "expired" || m.invite === "none" ? "Send new link" : "Resend link"}</Button>
    </div>
  );
}

export function InviteTracker({ onChanged }) {
  const { data, reload } = useApi("/admin/member-tools/invites");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(() => localStorage.getItem("invite-tracker-open") !== "0");
  const toggle = () => { setOpen((o) => { localStorage.setItem("invite-tracker-open", o ? "0" : "1"); return !o; }); };
  const refresh = async (m) => {
    setBusy(m.id);
    try { const { data: r } = await api.post(`/admin/member-tools/${m.id}/send-invite`); toast[r.invite === "sent" ? "success" : "error"](r.invite === "sent" ? `Fresh 24-hour link sent to ${m.name}` : "Email could not be delivered"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (!data || data.length === 0) return null;
  return (
    <div className="mb-8" data-testid="invite-tracker">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={toggle} className="flex items-center gap-2 text-left" aria-expanded={open} data-testid="invite-tracker-toggle">
          <ChevronDown size={18} className={`text-gold transition-transform ${open ? "" : "-rotate-90"}`} />
          <h3 className="font-serif-display text-xl font-bold text-crimson">Invites awaiting password setup <span className="ml-1 font-mono-accent text-sm text-muted-foreground">({data.length})</span></h3>
          {!open && <span className="text-xs text-muted-foreground">— click to expand</span>}
        </button>
        <InviteSweep onDone={reload} />
      </div>
      {open && <div className="mt-3 space-y-2" data-testid="invite-tracker-list">
        {data.map((m) => <InviteRow key={m.id} m={m} busy={busy === m.id} onRefresh={() => refresh(m)} />)}
      </div>}
    </div>
  );
}

const PROTECTED_ROLES = ["Admin", "President", "Vice President", "General Secretary"];

function MergeTotals({ preview }) {
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="merge-totals">
      {[["keep", "Keeps"], ["merge", "Removed"]].map(([key, tag]) => { const a = preview[key]; return (
        <div key={key} className={`rounded-xl border p-3 text-sm ${key === "keep" ? "border-palm/50 bg-palm/5" : "border-red-300 bg-red-50"}`} data-testid={`merge-totals-${key}`}>
          <p className="eyebrow text-[10px] text-gold">{tag}</p>
          <p className="truncate font-semibold">{a.name} <span className="font-mono-accent text-xs text-muted-foreground">{a.member_id}</span></p>
          <dl className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Balance owed</dt><dd className={`text-right font-mono-accent font-semibold ${a.balance > 0 ? "text-crimson" : "text-palm"}`} data-testid={`merge-balance-${key}`}>${a.balance.toFixed(2)}</dd>
            <dt className="text-muted-foreground">Payments</dt><dd className="text-right font-mono-accent" data-testid={`merge-payments-${key}`}>{a.payments} · ${a.paid_total.toFixed(2)}</dd>
            <dt className="text-muted-foreground">Welfare claims</dt><dd className="text-right font-mono-accent">{a.claims}</dd>
            <dt className="text-muted-foreground">RSVPs</dt><dd className="text-right font-mono-accent">{a.rsvps}</dd>
            <dt className="text-muted-foreground">Joined</dt><dd className="text-right font-mono-accent">{a.join_date || "—"}{a.grandfathered ? " · GF" : ""}</dd>
          </dl>
        </div>); })}
      <p className="col-span-2 text-xs text-muted-foreground">After merging, <strong>{preview.keep.name}</strong> will have {preview.keep.payments + preview.merge.payments} payments totalling ${(preview.keep.paid_total + preview.merge.paid_total).toFixed(2)}; the balance is recomputed from the combined history.</p>
    </div>
  );
}

function MergeWarning({ k, d }) {
  return (
    <div className="rounded-xl border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="merge-preview">
      <p className="flex items-center gap-2 font-semibold"><AlertTriangle size={15} /> What happens</p>
      <ul className="mt-1 list-disc pl-5 text-xs">
        <li>Payments, offline claims, welfare claims, balance adjustments, RSVPs, attendance and notifications of <strong>{d.name}</strong> move to <strong>{k.name}</strong>.</li>
        <li>Missing profile details (phone, address, DOB, photo) are filled from the duplicate; earliest join date is kept.</li>
        <li><strong>{d.email}</strong> can no longer sign in. A copy is stored in the audit archive.</li>
      </ul>
    </div>
  );
}

function useMergePreview(keep, merge) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!keep || !merge) { setPreview(null); return; }
    api.get("/admin/member-tools/merge-preview", { params: { keep_id: keep, merge_id: merge } }).then(({ data }) => setPreview(data)).catch(() => setPreview(null));
  }, [keep, merge]);
  return preview;
}

export function MergeMembers({ members, onMerged }) {
  const [open, setOpen] = useState(false);
  const [keep, setKeep] = useState("");
  const [merge, setMerge] = useState("");
  const [busy, setBusy] = useState(false);
  const preview = useMergePreview(keep, merge);
  const opt = (m) => <SelectItem key={m.id} value={m.id}>{m.name} — {m.email} ({m.member_id})</SelectItem>;
  const keepOptions = useMemo(() => (members || []).filter((m) => m.id !== merge), [members, merge]);
  const dupOptions = useMemo(() => (members || []).filter((m) => m.id !== keep && !PROTECTED_ROLES.includes(m.role)), [members, keep]);
  const k = members?.find((m) => m.id === keep); const d = members?.find((m) => m.id === merge);
  const run = async () => {
    if (!window.confirm(`Merge "${d.name}" INTO "${k.name}"?\n\nAll payments, claims, RSVPs and history move to ${k.name}. ${d.name}'s login (${d.email}) will be removed. This cannot be undone.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/member-tools/merge", { keep_id: keep, merge_id: merge }); const moved = Object.entries(data.moved).map(([c, n]) => `${n} ${c.replace("_", " ")}`).join(", "); toast.success(`Merged. Moved: ${moved || "nothing"}`); setOpen(false); setKeep(""); setMerge(""); onMerged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="gap-2" data-testid="merge-btn"><GitMerge size={16} /> Merge duplicates</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="merge-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-crimson">Merge duplicate accounts</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><p className="mb-1 text-sm font-semibold">Keep this account (survives)</p>
              <Select value={keep} onValueChange={setKeep}><SelectTrigger data-testid="merge-keep"><SelectValue placeholder="Choose the account to keep" /></SelectTrigger><SelectContent>{keepOptions.map(opt)}</SelectContent></Select></div>
            <div><p className="mb-1 text-sm font-semibold">Merge this duplicate into it (removed)</p>
              <Select value={merge} onValueChange={setMerge}><SelectTrigger data-testid="merge-dup"><SelectValue placeholder="Choose the duplicate" /></SelectTrigger><SelectContent>{dupOptions.map(opt)}</SelectContent></Select></div>
            {preview && <MergeTotals preview={preview} />}
            {k && d && <MergeWarning k={k} d={d} />}
            <Button onClick={run} disabled={!keep || !merge || busy} className="w-full bg-crimson" data-testid="merge-confirm"><GitMerge size={16} className="mr-2" /> Merge accounts</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
